/**
 * Turning a drawn icon into a solid object.
 *
 * An item held in the hand cannot be the flat picture the inventory uses.
 * Standing a sixteen-pixel drawing up in the air gives you a card: from the
 * side it is a line, and from any angle it reads as a picture of a шабля
 * rather than as a шабля. That was the complaint, and cutting the background
 * away only got half of it - a cut-out card is still a card.
 *
 * So every opaque pixel becomes a block of its own, one pixel thick, and what
 * you see at an angle is the *edge* of the drawing: the stepped silhouette
 * and the shaded sides that say the thing has a near face and a far face.
 *
 * **Not one cuboid per pixel.** Two hundred and fifty-six little boxes for a
 * sword is a great deal of geometry to say something two quads can say. The
 * front and back of the sprite are one quad each, with the transparent parts
 * cut away by the material; only the *rim* is built pixel by pixel, and only
 * where an opaque pixel meets a transparent one. A шабля comes out at about a
 * hundred faces instead of fifteen hundred.
 *
 * Pure: pixels in, numbers out. No THREE, no canvas, no atlas - the caller
 * decides where in a sheet these coordinates land.
 */

export interface SpriteMesh {
  /** xyz triples. The sprite spans -0.5..0.5 in x and y. */
  readonly positions: number[];
  /** uv pairs, 0..1 within the tile. */
  readonly uvs: number[];
  readonly indices: number[];
}

/** Below this an item pixel counts as nothing at all. */
export const OPAQUE_AT = 128;

/**
 * Build the solid form of one drawn tile.
 *
 * @param pixels RGBA, `size * size * 4`, row 0 at the top - which is how a
 *   canvas hands them over and how `paintTile` writes them.
 * @param size the tile's width in pixels.
 * @param thickness how deep it stands, as a fraction of its own width.
 */
export function extrudeSprite(
  pixels: Uint8Array | Uint8ClampedArray, size: number, thickness: number,
): SpriteMesh {
  const positions: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];

  const half = thickness / 2;
  const step = 1 / size;
  const solid = (px: number, py: number): boolean => {
    if (px < 0 || py < 0 || px >= size || py >= size) return false;
    return (pixels[(py * size + px) * 4 + 3] ?? 0) >= OPAQUE_AT;
  };

  /** A quad, given its four corners and the texture point they all take. */
  const face = (
    corners: readonly [number, number, number][],
    uv: readonly [number, number][],
  ): void => {
    const base = positions.length / 3;
    for (let i = 0; i < 4; i++) {
      positions.push(corners[i]![0], corners[i]![1], corners[i]![2]);
      uvs.push(uv[i]![0], uv[i]![1]);
    }
    indices.push(base, base + 1, base + 2, base, base + 2, base + 3);
  };

  /*
   * The two big faces.
   *
   * Whole-sprite quads with the tile stretched across them; the material's
   * alpha test cuts the background away, which is the same trick that makes
   * leaves work and costs two quads instead of five hundred.
   */
  face(
    [[-0.5, -0.5, half], [0.5, -0.5, half], [0.5, 0.5, half], [-0.5, 0.5, half]],
    [[0, 0], [1, 0], [1, 1], [0, 1]],
  );
  face(
    [[0.5, -0.5, -half], [-0.5, -0.5, -half], [-0.5, 0.5, -half], [0.5, 0.5, -half]],
    [[1, 0], [0, 0], [0, 1], [1, 1]],
  );

  /*
   * And the rim.
   *
   * One quad wherever an opaque pixel has a see-through neighbour, taking
   * that pixel's own colour. Sampled at the middle of the pixel rather than
   * at its corner: a wall built from a corner sample picks up whatever is in
   * the next pixel along and the edge of a blade comes out fringed.
   */
  for (let py = 0; py < size; py++) {
    for (let px = 0; px < size; px++) {
      if (!solid(px, py)) continue;

      const x0 = -0.5 + px * step;
      const x1 = x0 + step;
      // Row 0 is the top of the picture and +y is up.
      const y1 = 0.5 - py * step;
      const y0 = y1 - step;
      const u = (px + 0.5) * step;
      const v = 1 - (py + 0.5) * step;
      const flat: [number, number][] = [[u, v], [u, v], [u, v], [u, v]];

      if (!solid(px - 1, py)) {
        face([[x0, y0, -half], [x0, y0, half], [x0, y1, half], [x0, y1, -half]], flat);
      }
      if (!solid(px + 1, py)) {
        face([[x1, y0, half], [x1, y0, -half], [x1, y1, -half], [x1, y1, half]], flat);
      }
      // py - 1 is the row above, which is upwards on screen.
      if (!solid(px, py - 1)) {
        face([[x0, y1, half], [x1, y1, half], [x1, y1, -half], [x0, y1, -half]], flat);
      }
      if (!solid(px, py + 1)) {
        face([[x0, y0, -half], [x1, y0, -half], [x1, y0, half], [x0, y0, half]], flat);
      }
    }
  }

  return { positions, uvs, indices };
}

/** How many quads a mesh has. For tests, and for arguing about the cost. */
export function faceCount(mesh: SpriteMesh): number {
  return mesh.indices.length / 6;
}

/**
 * How full a shape has to be before it counts as a lump rather than a tool.
 *
 * A шабля drawn corner to corner fills about a fifth of the box it sits in; a
 * loaf or an ingot fills most of it. The number is the line between "this has
 * a handle at one end" and "this is a thing you hold in your fist".
 */
export const COMPACT_AT = 0.45;

/**
 * Where the hand takes hold, in the same -0.5..0.5 the mesh uses.
 *
 * Tools are drawn hilt-to-tip along the diagonal, handle at the bottom left -
 * that is the convention every one of these icons follows. Held at the middle
 * of the picture, which is where a centred mesh puts the fist, a sabre is
 * gripped halfway up its blade.
 *
 * A lump has no handle and is held in the middle, so the rule has to be able
 * to tell the two apart. How much of its own bounding box the drawing fills
 * does it: a diagonal fills a fifth, a loaf fills most.
 */
export function gripOf(
  pixels: Uint8Array | Uint8ClampedArray, size: number,
  /** Where the handle is: a sabre's hilt at the bottom-left, or the foot of a shaft. */
  from: "bottomLeft" | "bottom" = "bottomLeft",
): { x: number; y: number } {
  const solid = (px: number, py: number): boolean =>
    (pixels[(py * size + px) * 4 + 3] ?? 0) >= OPAQUE_AT;

  let minX = size, maxX = -1, minY = size, maxY = -1, count = 0;
  for (let py = 0; py < size; py++) {
    for (let px = 0; px < size; px++) {
      if (!solid(px, py)) continue;
      count++;
      minX = Math.min(minX, px); maxX = Math.max(maxX, px);
      minY = Math.min(minY, py); maxY = Math.max(maxY, py);
    }
  }
  // Nothing drawn: the middle is as good an answer as any.
  if (count === 0) return { x: 0, y: 0 };

  const toSprite = (px: number, py: number): { x: number; y: number } => ({
    x: -0.5 + (px + 0.5) / size,
    // Row 0 is the top of the picture and +y is up.
    y: 0.5 - (py + 0.5) / size,
  });

  const fill = count / ((maxX - minX + 1) * (maxY - minY + 1));
  if (fill >= COMPACT_AT) return toSprite((minX + maxX) / 2, (minY + maxY) / 2);

  /*
   * The end of the handle: the drawn pixel nearest the bottom left.
   *
   * Then pulled a little way back along the shape, because a hand closed on
   * the very last pixel of a hilt looks like it is about to drop it.
   */
  if (from === "bottom") {
    // The foot of the shaft: the lowest row that has anything in it, and
    // the middle of what is there, a little way up into the wood.
    let sum = 0, n = 0;
    for (let px = 0; px < size; px++) if (solid(px, maxY)) { sum += px; n++; }
    const along = 0.18;
    return toSprite(sum / Math.max(1, n), maxY + ((minY + maxY) / 2 - maxY) * along);
  }
  let bestX = minX, bestY = maxY, best = Infinity;
  for (let py = 0; py < size; py++) {
    for (let px = 0; px < size; px++) {
      if (!solid(px, py)) continue;
      const away = (px - minX) + (maxY - py);
      if (away < best) { best = away; bestX = px; bestY = py; }
    }
  }
  const along = 0.18;
  return toSprite(
    bestX + ((minX + maxX) / 2 - bestX) * along,
    bestY + ((minY + maxY) / 2 - bestY) * along,
  );
}
